import {
  addMinutes,
  endOfMinute,
  getMinutes,
  startOfMinute,
  subMinutes,
} from 'date-fns'
import React from 'react'

import { useRoot } from '../app/useRoot'
import {
  QDelayedOrderIntervals,
  useMOrderDeliveryTime,
  useQDelayedOrder,
} from '../graph/generated'
import { RM } from '../tools/ramda'
import { useShopStatus } from './useShopStatus'

type TOptions = {
  forBranchId?: ID
}

const STEP_SIZE_MINUTES = 15

export function useDelayedOrder({ forBranchId }: TOptions = {}) {
  const { order, cart } = useRoot()

  const branchId = forBranchId ?? order.branchId

  const skip = !branchId

  const { getStash } = useQDelayedOrder({ branchId: branchId! }, { skip })
  const delayedOrder = getStash(data => data.delayedOrder)

  const { decision } = useShopStatus({ status: delayedOrder })
  const shopIsClosed = decision === 'Closed' || decision === 'OpeningSoon'

  const getSteps = React.useCallback(() => {
    if (delayedOrder === null) {
      return null
    }
    return createSteps(delayedOrder.intervals)
  }, [delayedOrder])

  const canBeDelayed = React.useCallback(() => {
    return delayedOrder !== null && delayedOrder.intervals.length > 0
  }, [delayedOrder])

  const [updateOrder] = useMOrderDeliveryTime()

  const changeDeliveryTime = React.useCallback(
    (deliverAt: Maybe<Date>) => {
      updateOrder({
        orderId: order.orderId!,
        withGoods: cart.withGoods,
        deliverAt,
      })
      order.setDeliverAt(deliverAt)
    },
    [cart, order, updateOrder],
  )

  return {
    getSteps,
    canBeDelayed,
    isEnabled: Boolean(delayedOrder) && delayedOrder!.enabled,
    changeDeliveryTime,
    shopIsClosed,
  }
}

function createSteps(intervals: RoA<QDelayedOrderIntervals>) {
  return RM.flatten(intervals.map(createTimeSteps))
}

function createTimeSteps({ start, end }: QDelayedOrderIntervals) {
  let current = startOfMinute(timeClampUp(start, STEP_SIZE_MINUTES))
  const steps: Date[] = []
  const endMinute = endOfMinute(subMinutes(end, 1))
  do {
    steps.push(current)
    current = addMinutes(current, STEP_SIZE_MINUTES)
  } while (current <= endMinute)
  return steps
}

function timeClampUp(time: Date, minuteStep: number) {
  return addMinutes(timeClampDown(time, minuteStep), minuteStep)
}

function timeClampDown(time: Date, minuteStep: number) {
  return subMinutes(time, getMinutes(time) % minuteStep)
}
